/**
 * Smoothly scrolls an element into view and temporarily highlights it once
 * the element becomes visible in the viewport.
 *
 * Uses `IntersectionObserver` to wait until the element is sufficiently
 * visible before applying the highlight effect. If `IntersectionObserver`
 * is unavailable, the highlight is applied immediately after scrolling.
 *
 * Returns the created `IntersectionObserver` so callers may manually
 * disconnect it during component teardown if needed.
 *
 * @param el The element to scroll into view and highlight.
 * @param opts Configuration options.
 * @param opts.delay Duration in milliseconds before the highlight styles are removed.
 * Defaults to `500`.
 * @param opts.scroll Options passed to `Element.scrollIntoView()`.
 * Defaults to `{ behavior: 'smooth', block: 'center' }`.
 * @param opts.observer Options passed to `IntersectionObserver`.
 * Defaults to `{ threshold: 0.6, root: null }`.
 * @param opts.onHighlighted Success callback executed immediately after the element is highlighted.
 *
 * @returns The created `IntersectionObserver`, or `undefined` if the browser
 * does not support `IntersectionObserver`.
 *
 * @example
 * ```ts
 * this.intersectionObserver = scrollIntoViewAndHighlight(
 *   workorderRef.nativeElement,
 *   {
 *     scroll: {
 *       behavior: 'smooth',
 *       block: 'center',
 *     },
 *   },
 * );
 * ```
 */
export function scrollIntoViewAndHighlight(
  el: HTMLElement,
  opts: {
    delay?: number;
    scroll?: ScrollIntoViewOptions;
    observer?: IntersectionObserverInit;
    onHighlighted?: () => void;
  } = {},
) {

  const {
    delay = 500,
    scroll = {},
    observer = {},
    onHighlighted,
  } = opts;

  const highlight = () => {
    highlightElement(el, delay);
    onHighlighted?.();
  }

  el.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    ...scroll,
  });

  if (!('IntersectionObserver' in window)) {
    highlight();
    return;
  }

  const intersectionObserver = new IntersectionObserver(
    ([ entry ], obs) => {
      if (entry.isIntersecting) {
        highlight();
        obs.disconnect();
      }
    },
    {
      threshold: 0.6,
      root: null,
      ...observer,
    },
  );

  intersectionObserver.observe(el);

  return intersectionObserver;
}

export function highlightElement(el: HTMLElement, delay: number) {
  el.style['transition'] = 'box-shadow 0.2s, border-color 0.2s';
  el.style['box-shadow'] = '0 0 0 0.2rem var(--primary-50)';
  el.style['border-color'] = 'var(--primary-50)';

  setTimeout(() => {
    el.style['box-shadow'] = '';
    el.style['border-color'] = '';
    el.style.transition = '';
  }, delay);
}
